/**
 * Global toast queue for transient storefront feedback.
 * Mount ToastProvider under ThemeProvider; consume via useToast().
 */

'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import styled from '@emotion/styled';
import {
  Toast,
  type ToastVariant,
} from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n';
import { TOAST_ACCESSIBILITY_VIEWPORT } from './i18.keys';

const MAX_TOASTS = 5;
const DEFAULT_DURATION_MS = 5000;

export type ToastInput = string | { message: string; duration?: number };

export type ToastItem = {
  id: string;
  message: string;
  variant: ToastVariant;
  duration: number;
};

type ToastMethods = {
  success: (input: ToastInput) => string;
  error: (input: ToastInput) => string;
  info: (input: ToastInput) => string;
  warning: (input: ToastInput) => string;
};

type ToastContextValue = {
  toast: ToastMethods;
  addToast: (toast: Omit<ToastItem, 'id'> & { id?: string }) => string;
  removeToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const ToastViewport = styled.div`
  position: fixed;
  z-index: ${({ theme }) => theme?.zIndex?.toast ?? 60};
  bottom: 24px;
  right: 24px;
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  width: min(400px, calc(100vw - 48px));
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

  @media (max-width: 639px) {
    right: auto;
    left: 50%;
    transform: translateX(-50%);
  }
`;

function normalizeInput(
  input: ToastInput,
  variant: ToastVariant,
): Omit<ToastItem, 'id'> {
  const message = typeof input === 'string' ? input : input.message;
  const explicitDuration =
    typeof input === 'string' ? undefined : input.duration;
  const duration =
    explicitDuration ?? (variant === 'error' ? 0 : DEFAULT_DURATION_MS);
  return { message, variant, duration };
}

function createToastId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function ToastProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  const clearTimer = useCallback((id: string) => {
    const timer = timeoutsRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const removeToast = useCallback(
    (id: string) => {
      clearTimer(id);
      setToasts((prev) => prev.filter((item) => item.id !== id));
    },
    [clearTimer],
  );

  const addToast = useCallback(
    (toast: Omit<ToastItem, 'id'> & { id?: string }) => {
      const id = toast.id ?? createToastId();
      setToasts((prev) => {
        const next = [...prev, { ...toast, id }];
        if (next.length <= MAX_TOASTS) {
          return next;
        }
        const evicted = next.slice(0, next.length - MAX_TOASTS);
        for (const item of evicted) {
          clearTimer(item.id);
        }
        return next.slice(-MAX_TOASTS);
      });

      if (toast.duration > 0) {
        clearTimer(id);
        const timer = setTimeout(() => {
          timeoutsRef.current.delete(id);
          setToasts((prev) => prev.filter((item) => item.id !== id));
        }, toast.duration);
        timeoutsRef.current.set(id, timer);
      }

      return id;
    },
    [clearTimer],
  );

  const toast = useMemo<ToastMethods>(
    () => ({
      success: (input) => addToast(normalizeInput(input, 'success')),
      error: (input) => addToast(normalizeInput(input, 'error')),
      info: (input) => addToast(normalizeInput(input, 'info')),
      warning: (input) => addToast(normalizeInput(input, 'warning')),
    }),
    [addToast],
  );

  const value = useMemo(
    () => ({ toast, addToast, removeToast }),
    [toast, addToast, removeToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.length > 0 ? (
        <ToastViewport aria-label={t(TOAST_ACCESSIBILITY_VIEWPORT)}>
          {toasts.map((item) => (
            <Toast
              key={item.id}
              embedded
              variant={item.variant}
              autoDismissMs={0}
              onDismiss={() => removeToast(item.id)}
            >
              {item.message}
            </Toast>
          ))}
        </ToastViewport>
      ) : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}
